var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
const debug = true;
let debugLog = undefined;
function isAshlyChannelConfig(config) {
    return typeof config === 'string';
}
function isAshlyPresetMuteConfig(config) {
    return (typeof config === 'object' &&
        'mutePreset' in config &&
        typeof config.mutePreset === 'string' &&
        'muteStatus' in config &&
        typeof config.muteStatus === 'string' &&
        'unmutePreset' in config &&
        typeof config.unmutePreset === 'string');
}
function isResp(schema, resp) {
    var _a;
    const result = schema.safeParse(resp);
    if (!result.success && debug && debugLog) {
        debugLog.debug(`Zod errors: ${JSON.stringify((_a = result === null || result === void 0 ? void 0 : result.error) === null || _a === void 0 ? void 0 : _a.issues, null, 4)}`);
    }
    return result.success;
}
const commandErrorResponse = z.object({
    statusCode: z.number(),
    error: z.string(),
});
const dspBlockResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        id: z.string(),
        sequence: z.number(),
        isWorkingSettings: z.boolean(),
        DSPChainId: z.string(),
        DSPBlockTypeId: z.string(),
    })),
});
const dspChannelResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        id: z.string(),
        defaultName: z.string(),
        name: z.string(),
        type: z.string(),
        baseType: z.string(),
        channelNumber: z.number(),
    })),
});
const dspMixerConfigResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        id: z.string(),
        value: z.number().or(z.boolean()).or(z.string()).nullable(),
        index: z.number().nullable(),
        channelId: z.string().nullable(),
        DSPMixerConfigId: z.string(),
        DSPMixerConfigParameterTypeId: z.string(),
    })),
});
const loginResponseFailure = z.object({
    statusCode: z.number(),
    error: z.string(),
    message: z.string(),
});
const loginResponseSuccess = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        loggedIn: z.boolean(),
        enabledPermissions: z.array(z.string()),
        enabledRemotePermissions: z.array(z.string()).optional(),
        lastLogin: z.string(),
        uniqueId: z.string(),
        keepLoggedIn: z.boolean(),
    })),
});
const mixerGetAllMuteVolumeResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        id: z.string(),
        value: z.boolean().or(z.number()),
    })),
});
const muteGetAllResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        id: z.string(),
        muted: z.boolean(),
    })),
});
const muteMixerSetResponse = z.object({
    success: z.boolean(),
    data: z.object({
        value: z.string(),
    }),
});
const muteSetErrorResponse = z.object({
    statusCode: z.number(),
    error: z.string(),
    message: z.string(),
});
const muteSetResponse = z.object({
    success: z.boolean(),
    data: z.object({
        id: z.string(),
        isWorkingSettings: z.boolean(),
        muted: z.boolean(),
        mixerId: z.string().nullable(),
        DSPChannelId: z.string(),
        presetId: z.string().nullable(),
    }),
});
const powerGetResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        powerState: z.string(),
    })),
});
const powerSetResponse = z.object({
    success: z.boolean(),
    data: z.object({
        powerState: z.string().optional(),
        msg: z.string().optional(),
    }),
});
const presetGetResponse = z.object({
    success: z.boolean(),
    data: z.array(z
        .object({
        id: z.string(),
        name: z.string(),
        type: z.string(),
    })
        .nullable()),
});
const presetRecallResponse = z.object({
    success: z.boolean(),
    data: z.object({}),
});
const systemInfoResponse = z.object({
    success: z.boolean(),
    data: z.array(z.object({
        id: z.number(),
        name: z.string(),
        group: z.string(),
        deviceType: z.string(),
        platformType: z.string(),
        notes: z.string(),
        softwareRevision: z.string(),
        hardwareRevision: z.string(),
    })),
});
const volumeSetResponse = z.object({
    success: z.boolean(),
    data: z.object({
        value: z.string().or(z.number()),
    }),
});
const allowedMuteTypes = ['inputOutput', 'crossPoint'];
const allowedVolumeTypes = ['procBlockGain', 'crossPoint'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: allowedMuteTypes,
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: allowedMuteTypes,
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: ['preset'],
    receiveGain: [],
    soundReinforcementMute: allowedMuteTypes,
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
class Channel {
    constructor(type, key, name) {
        this.type = type;
        this.key = key;
        this.name = name;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
    }
    getFader(http, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    getMute(http, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    httpPostJson(http, cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            const resp = yield http.requestJson('post', `:8000/v1.0-beta${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    isAllowed(filter) {
        return allowedTypes[filter].includes(this.type);
    }
    loadPreset(http, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`preset not supported for ${this.key}`);
        });
    }
    matches(key) {
        return this.key === key;
    }
    setFader(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setMute(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
}
class Channels {
    constructor() {
        this.channels = [];
    }
    get areAnyConfigured() {
        for (const channel of this.channels) {
            if (channel.isMuteConfigured || channel.isVolumeConfigured)
                return true;
        }
        return false;
    }
    addChannel(chan) {
        this.channels.push(chan);
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: type === 'preset',
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    getByKey(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getChannel(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel;
        }
        return undefined;
    }
    getConfiguredChannels(control) {
        return this.channels.filter(channel => control === 'mute' ? channel.isMuteConfigured : channel.isVolumeConfigured);
    }
    getDetected() {
        const detectedChannels = [];
        for (const channel of this.channels) {
            const detectedChannel = {
                channel: channel.key,
                filters: this.computeFilters(channel.type),
                name: channel.name,
                type: channel.type,
            };
            if (channel.name)
                detectedChannel.name = channel.name;
            detectedChannels.push(detectedChannel);
        }
        return detectedChannels;
    }
}
class InputOutputChannel extends Channel {
    constructor(chanType, id, chanName) {
        super(chanType, id, chanName);
    }
    setMute(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpPostJson(http, {
                cmd: `/simplecontrol/workingsettings/dsp/chain/mute/${this.key}`,
                data: JSON.stringify({ muted: value }),
            }, muteSetResponse.or(muteSetErrorResponse), tags);
            return 'success' in resp ? value : undefined;
        });
    }
}
// Processing Blocks provide a gain gain block (with no mute control) in the signal processing chain
// for an input or output. We store the associated input/output channel to support mute.
class ProcessingBlockChannel extends Channel {
    constructor(chanType, id, chanName, inOrOutChannel) {
        super(chanType, id, chanName);
        this.inOrOutChannel = inOrOutChannel;
    }
    setFader(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpPostJson(http, {
                cmd: `/workingsettings/dsp/block/parameter/${this.key}.Level`,
                data: JSON.stringify({ value: value }),
            }, volumeSetResponse, tags);
            return resp.success ? value : undefined;
        });
    }
    // A processing block gain block doesn't support mute, so mute the associated input or output
    // channel that feeds into or from the processing block.
    setMute(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            let result;
            if (this.inOrOutChannel) {
                result = yield this.inOrOutChannel.setMute(http, value, tags);
            }
            return result;
        });
    }
}
// Crosspoints are the control points between an input and a mixer output.
class CrossPointChannel extends Channel {
    constructor(chanType, id, chanName) {
        super(chanType, id, chanName);
    }
    setFader(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpPostJson(http, {
                cmd: `/workingsettings/dsp/mixer/config/parameter/${this.key}.Source Level`,
                data: JSON.stringify({ value: value }),
            }, volumeSetResponse, tags);
            return resp.success ? value : undefined;
        });
    }
    setMute(http, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpPostJson(http, {
                cmd: `/workingsettings/dsp/mixer/config/parameter/${this.key}.Source Mute`,
                data: JSON.stringify({ value: `${value}` }),
            }, muteMixerSetResponse, tags);
            return resp.success ? value : undefined;
        });
    }
}
class PresetChannel extends Channel {
    constructor(chanType, id, chanName) {
        super(chanType, id, chanName);
    }
    loadPreset(http, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.httpPostJson(http, { cmd: `/preset/recall/${this.name}` }, presetRecallResponse, tags);
        });
    }
}
export class AshlyMxaDsp {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = new Channels();
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const detectedChannels = this.channels.getDetected();
                this.log.debug(`Detected channels = ${JSON.stringify(detectedChannels, null, 4)}`);
                return detectedChannels;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: '/system/info' }, systemInfoResponse);
                return resp.success ? resp.data[0].softwareRevision : this.state.firmwareVersion.value;
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                // Mutes are determined with polling.
                return this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                if (channel.presetMute === undefined) {
                    const retValue = yield channel.setMute(this.http, value, tags);
                    return retValue !== null && retValue !== void 0 ? retValue : this.state.mutes.values[key];
                }
                else {
                    if (value) {
                        yield channel.presetMute.mute.loadPreset(this.http, tags);
                    }
                    else {
                        yield channel.presetMute.unmute.loadPreset(this.http, tags);
                    }
                }
                return value;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: '/simplecontrol/system/frontPanel/info/power' }, powerGetResponse.or(commandErrorResponse));
                if ('success' in resp) {
                    return resp.success
                        ? resp.data[0].powerState.toUpperCase() === 'ON'
                        : this.state.power.value;
                }
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const cmd = {
                    cmd: '/simplecontrol/system/frontPanel/info/power',
                    data: `{value: ${power ? 'On' : 'Off'}}`,
                };
                const resp = yield this.httpPostJson(cmd, powerSetResponse, tags);
                return resp.success ? power : this.state.power.value;
            }),
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                // Volumes are determined from polling
                return this.state.volumes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                yield this.syncVolumeMute(channel, value);
                const retValue = yield channel.setFader(this.http, value, tags);
                return retValue ? value : this.state.volumes.values[key];
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 12, min: -12, round: 1 };
                return { max: 12, min: -38, round: 1 }; // volume
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.getAllMutesAndGains();
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            debugLog = this.log;
            this.channels = new Channels();
            yield this.getChannels(['configure']); // rediscover channels
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.tags = ['initialize'];
            try {
                const resp = yield this.httpPostJson({
                    cmd: '/session/login',
                    data: JSON.stringify({
                        username: (_a = credentials.username) !== null && _a !== void 0 ? _a : 'SimpleControl',
                        password: (_b = credentials.password) !== null && _b !== void 0 ? _b : 'secret',
                        keepLoggedIn: false,
                    }),
                }, loginResponseSuccess.or(loginResponseFailure), ['initialize']);
                if ('success' in resp) {
                    return 'connected';
                }
                else if ('error' in resp && resp.error.toLowerCase() === 'unauthorized') {
                    return 'authFailed';
                }
                return 'connectFailed';
            }
            catch (error) {
                if (error instanceof TransportError && error.code === 'sendTimeout') {
                    return 'connectFailed';
                }
                else {
                    return 'authFailed';
                }
            }
            return 'connectFailed';
        });
    }
    // other class methods
    // Gets the input/output mutes/gains, dsp block gains, and crosspoint mutes and gains.
    getAllMutesAndGains() {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.tags = ['poll'];
            // Get input/output channel mutes.
            const resp = yield this.httpGetJson({ cmd: `/simplecontrol/workingsettings/dsp/chain/mute/` }, muteGetAllResponse);
            if (resp.success) {
                for (const chan of resp.data) {
                    const channel = this.channels.getChannel(chan.id);
                    if (channel === null || channel === void 0 ? void 0 : channel.isMuteConfigured) {
                        this.state.mutes.update(chan.id, chan.muted);
                    }
                }
            }
            // Get the crosspoint mixer mutes and levels.
            const mixerResp = yield this.httpGetJson({ cmd: `/simplecontrol/workingsettings/dsp/mixer/sourceMutesAndLevels` }, mixerGetAllMuteVolumeResponse);
            if (mixerResp.success) {
                for (const chan of mixerResp.data) {
                    // These responses use the fields SourceMute and SourceLevel with no space, so add a
                    // space between Source and Mute to match channel ids.
                    if (chan.id.includes('SourceMute')) {
                        const chanId = chan.id.substring(0, chan.id.length - 11); // Remove 'SourceMute'
                        const channel = this.channels.getByKey(chanId);
                        if ((channel === null || channel === void 0 ? void 0 : channel.isMuteConfigured) && typeof chan.value === 'boolean') {
                            this.state.mutes.update(chanId, chan.value);
                        }
                    }
                    else if (chan.id.includes('SourceLevel')) {
                        const chanId = chan.id.substring(0, chan.id.length - 12); // Remove 'SourceLevel'
                        const channel = this.channels.getByKey(chanId);
                        if ((channel === null || channel === void 0 ? void 0 : channel.isVolumeConfigured) && typeof chan.value === 'number') {
                            this.state.volumes.update(chanId, chan.value);
                        }
                    }
                }
            }
            // Get block gains.
            const gainResp = yield this.httpGetJson({ cmd: `/simplecontrol/workingsettings/dsp/block/gain/level` }, mixerGetAllMuteVolumeResponse);
            if (gainResp.success) {
                for (const chan of gainResp.data) {
                    const chanId = chan.id.substring(0, chan.id.length - 6); // Remove '.level'
                    const channel = this.channels.getByKey(chanId);
                    if ((channel === null || channel === void 0 ? void 0 : channel.isVolumeConfigured) && typeof chan.value === 'number') {
                        this.state.volumes.update(chanId, chan.value);
                    }
                }
            }
        });
    }
    // Gets the names and ids of all channels in the device.
    getChannels(tags) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return __awaiter(this, void 0, void 0, function* () {
            // Get the list of input and output channels.
            const resp = yield this.httpGetJson({ cmd: '/workingsettings/dsp/channel' }, dspChannelResponse, tags);
            if (resp.success) {
                for (const chan of (_a = resp === null || resp === void 0 ? void 0 : resp.data) !== null && _a !== void 0 ? _a : []) {
                    const chanId = chan.id;
                    this.channels.addChannel(new InputOutputChannel('inputOutput', chanId, (_c = (_b = chan.name) !== null && _b !== void 0 ? _b : chan.defaultName) !== null && _c !== void 0 ? _c : ''));
                }
            }
            // Get gain processing blocks that can be used for volumes.
            const procBlocksResp = yield this.httpGetJson({ cmd: '/workingsettings/dsp/block' }, dspBlockResponse, tags);
            if (procBlocksResp.success) {
                for (const chan of (_d = procBlocksResp === null || procBlocksResp === void 0 ? void 0 : procBlocksResp.data) !== null && _d !== void 0 ? _d : []) {
                    if (chan.DSPBlockTypeId === 'Gain') {
                        // Get the channel name from the input/output channel.
                        const inputOrOutputChannel = this.channels.getByKey(chan.DSPChainId);
                        const chanName = inputOrOutputChannel === null || inputOrOutputChannel === void 0 ? void 0 : inputOrOutputChannel.name;
                        this.channels.addChannel(new ProcessingBlockChannel('procBlockGain', chan.id, chanName ? `${chanName} (Gain Block ${chan.sequence})` : chan.id, inputOrOutputChannel));
                    }
                }
            }
            // Get the input to mixers 1, 2, 3, and 4 channels.
            const mixerResp = yield this.httpGetJson({ cmd: '/workingsettings/dsp/mixer/config/parameter' }, dspMixerConfigResponse, tags);
            if (mixerResp.success) {
                for (const chan of (_e = mixerResp === null || mixerResp === void 0 ? void 0 : mixerResp.data) !== null && _e !== void 0 ? _e : []) {
                    if (chan.DSPMixerConfigParameterTypeId === 'Mixer.Source Mute') {
                        // Get the channel name from the input/output channel.
                        const chanName = (_g = this.channels.getByKey((_f = chan === null || chan === void 0 ? void 0 : chan.channelId) !== null && _f !== void 0 ? _f : '')) === null || _g === void 0 ? void 0 : _g.name;
                        this.channels.addChannel(new CrossPointChannel('crossPoint', `${chan.DSPMixerConfigId}.${chan.channelId}`, chanName
                            ? `${chanName} to ${chan.DSPMixerConfigId.replace('Mixer.', 'Mixer ')}`
                            : chan.id));
                    }
                }
            }
            // Get preset information.
            const presetResp = yield this.httpGetJson({ cmd: '/preset' }, presetGetResponse, tags);
            if (presetResp.success) {
                for (const preset of (_h = presetResp === null || presetResp === void 0 ? void 0 : presetResp.data) !== null && _h !== void 0 ? _h : []) {
                    this.channels.addChannel(new PresetChannel('preset', (_j = preset === null || preset === void 0 ? void 0 : preset.id) !== null && _j !== void 0 ? _j : '', (_k = preset === null || preset === void 0 ? void 0 : preset.name) !== null && _k !== void 0 ? _k : 'Preset'));
                }
            }
        });
    }
    // For mute presets, return the key of mute status channel because that's where the mute and
    // unmute presets will be associated in the channel objects.
    getKey(config) {
        return isAshlyPresetMuteConfig(config) ? config.muteStatus : config;
    }
    hasRoomVolumeMute() {
        if (this.volumeConfig) {
            const chan = this.channels.getByKey(this.volumeConfig);
            return (chan === null || chan === void 0 ? void 0 : chan.type) ? allowedMuteTypes.includes(chan.type) : false;
        }
        else
            return false;
    }
    httpGetJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('get', `:8000/v1.0-beta${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    httpPostJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('post', `:8000/v1.0-beta${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    syncVolumeMute(channel, volume) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.volumeConfig === undefined || !this.hasRoomVolumeMute())
                return;
            const key = this.getKey(this.volumeConfig);
            const newVolumeMute = volume <= -38;
            if (channel.key !== key)
                return;
            if (this.state.mutes.values[key] !== newVolumeMute) {
                const value = yield this.mute.set(key, newVolumeMute, ['set.volumeMute']);
                this.state.mutes.update(key, value);
            }
        });
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (isAshlyPresetMuteConfig(config)) {
                const muteStatusChannel = this.channels.getByKey(config.muteStatus);
                const mutePresetChannel = this.channels.getByKey(config.mutePreset);
                const unmutePresetChannel = this.channels.getByKey(config.unmutePreset);
                if (muteStatusChannel === undefined ||
                    mutePresetChannel === undefined ||
                    unmutePresetChannel === undefined) {
                    return 'missing';
                }
                if (muteStatusChannel.type === 'preset' ||
                    mutePresetChannel.type !== 'preset' ||
                    unmutePresetChannel.type !== 'preset') {
                    return 'incompatible';
                }
                // Configure the mute and unmute presets.
                if (muteStatusChannel) {
                    muteStatusChannel.isMuteConfigured = true;
                    muteStatusChannel.presetMute = {
                        mute: mutePresetChannel,
                        unmute: unmutePresetChannel,
                    };
                }
                return 'ok';
            }
            else if (isAshlyChannelConfig(config)) {
                if (info.type === 'volume' && info.filter === 'amplifier')
                    this.volumeConfig = config;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    return 'missing';
                if (channel.type === 'preset')
                    return 'incompatible';
                if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                    if (config === this.volumeConfig && allowedMuteTypes.includes(channel.type)) {
                        channel.isMuteConfigured = true;
                    }
                }
                else if (info.type === 'mute') {
                    if (allowedMuteTypes.includes(channel.type))
                        channel.isMuteConfigured = true;
                }
                return 'ok';
            }
            return 'incompatible';
        });
    }
}
// static properties
AshlyMxaDsp.channelsConfigName = 'ashlyMxaDspControlChannels';
AshlyMxaDsp.driverType = 'httpDriver';
AshlyMxaDsp.key = '-Ne3aDybJFMi5N0bsbJH';
AshlyMxaDsp.httpRequestOptions = { scheme: 'http', idleTimeoutMs: 20000 };
